<template>
  <div class="handout-page">
    <div class="handout-layout">
      <el-card shadow="never" class="folder-panel">
        <div class="folder-header">
          <span class="folder-title">文件夹</span>
        </div>
        <div
          class="folder-item"
          :class="{ active: selectedFolderKey === 'all' }"
          @click="selectFolder('all')"
        >
          <i class="el-icon-files"></i>
          <span>全部讲义</span>
          <span class="folder-count">{{ folderSummary.totalCount || 0 }}</span>
        </div>
        <div
          class="folder-item"
          :class="{ active: selectedFolderKey === 'none' }"
          @click="selectFolder('none')"
        >
          <i class="el-icon-document"></i>
          <span>未分类</span>
          <span class="folder-count">{{ folderSummary.uncategorizedCount || 0 }}</span>
        </div>
        <el-tree
          v-if="folderTree.length"
          ref="folderTree"
          class="folder-tree"
          :data="folderTree"
          node-key="id"
          :props="{ label: 'name', children: 'children' }"
          highlight-current
          default-expand-all
          @node-click="onFolderNodeClick"
        >
          <span slot-scope="{ node, data }" class="tree-node">
            <i class="el-icon-folder"></i>
            <span class="tree-label">{{ node.label }}</span>
            <span class="folder-count">{{ data.handoutCount || 0 }}</span>
          </span>
        </el-tree>
        <div v-else class="folder-empty">暂无文件夹，点击「新建」创建</div>
        <div v-if="selectedFolderId > 0" class="folder-actions">
          <el-button type="text" size="mini" @click="openFolderDialog(selectedFolderId)">子文件夹</el-button>
          <el-button v-if="!isTeacherMode || !selectedFolderShared" type="text" size="mini" @click="renameFolder">重命名</el-button>
          <el-button v-if="!isTeacherMode || !selectedFolderShared" type="text" size="mini" class="danger-text" @click="deleteFolder">删除</el-button>
        </div>
      </el-card>

      <div class="handout-main">
        <el-card v-if="isTeacherMode && me" shadow="never" class="me-card">
          <span>任教学科：<b>{{ me.subjectName || '未配置' }}</b></span>
          <span v-if="exportQuotaLabel" class="export-quota-label">{{ exportQuotaLabel }}</span>
          <span class="me-hint">仅显示并管理本学科的讲义与文件夹。</span>
        </el-card>
        <el-card shadow="never" class="filter-card">
          <div class="filter-row">
            <el-input v-model="filterForm.keyword" placeholder="搜索讲义名称/说明" clearable prefix-icon="el-icon-search" style="width:220px" @keyup.enter.native="search" @clear="search" />
            <el-select v-model="filterForm.schoolYearId" placeholder="学年" clearable style="width:130px" @change="loadList">
              <el-option v-for="item in schoolYears" :key="item.id" :label="item.name" :value="item.id" />
            </el-select>
            <el-select v-model="filterForm.gradeId" placeholder="年级" clearable style="width:110px" @change="onGradeChange">
              <el-option v-for="item in grades" :key="item.id" :label="item.name" :value="item.id" />
            </el-select>
            <el-select v-model="filterForm.semesterId" placeholder="学期" clearable style="width:110px" @change="loadList">
              <el-option v-for="item in semesters" :key="item.id" :label="item.name" :value="item.id" />
            </el-select>
            <el-select v-if="!isTeacherMode" v-model="filterForm.subjectId" placeholder="学科" clearable style="width:110px" @change="loadList">
              <el-option v-for="item in subjects" :key="item.id" :label="item.name" :value="item.id" />
            </el-select>
            <el-select v-model="filterForm.lessonType" placeholder="课型" clearable style="width:120px" @change="loadList">
              <el-option v-for="item in lessonTypes" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
            <el-select v-model="filterForm.status" placeholder="状态" clearable style="width:110px" @change="loadList">
              <el-option label="草稿" :value="0" />
              <el-option label="已发布" :value="1" />
              <el-option label="已归档" :value="2" />
            </el-select>
            <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
            <el-button icon="el-icon-refresh-left" @click="resetFilters">重置</el-button>
            <el-dropdown trigger="click" class="create-dropdown" @command="handleCreateCommand">
              <el-button type="primary" icon="el-icon-plus">
                新建<i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="handout"><i class="el-icon-document"></i> 新建讲义</el-dropdown-item>
                <el-dropdown-item command="folder"><i class="el-icon-folder-add"></i> 新建文件夹</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
          <div v-if="currentFolderLabel" class="folder-breadcrumb">
            当前位置：<b>{{ currentFolderLabel }}</b>
          </div>
        </el-card>

        <div class="summary-strip">
          <div class="summary-item"><span>全部讲义</span><strong>{{ total }}</strong></div>
          <div class="summary-item"><span>已发布</span><strong>{{ publishedCount }}</strong></div>
          <div class="summary-item"><span>当前页</span><strong>{{ tableData.length }}</strong></div>
        </div>

        <div v-loading="loading" class="handout-list">
          <el-empty v-if="!loading && tableData.length === 0" description="暂无讲义，点击新建开始备课" />
          <div v-for="item in tableData" :key="item.id" class="handout-card">
            <div class="card-main">
              <div class="title-line">
                <span class="handout-title" @click="goDetail(item.id)">{{ item.title }}</span>
                <el-tag size="small" :type="statusType(item.status)">{{ statusLabel(item.status) }}</el-tag>
                <el-tag v-if="item.versionNo" size="mini" type="info">{{ item.versionNo }}</el-tag>
                <el-tag v-if="isTeacherMode && isOwnHandout(item)" size="mini" type="success">我编写的</el-tag>
                <el-tag v-else-if="isTeacherMode && !canEditHandout(item)" size="mini" type="info">仅可查看</el-tag>
              </div>
              <div class="meta-line">
                <span v-if="getSchoolYearName(item.schoolYearId)">{{ getSchoolYearName(item.schoolYearId) }}</span>
                <span v-if="getGradeName(item.gradeId)">{{ getGradeName(item.gradeId) }}</span>
                <span v-if="getSemesterName(item.semesterId)">{{ getSemesterName(item.semesterId) }}</span>
                <span v-if="getSubjectName(item.subjectId)">{{ getSubjectName(item.subjectId) }}</span>
                <span v-if="lessonTypeLabel(item.lessonType)">{{ lessonTypeLabel(item.lessonType) }}</span>
                <span>{{ item.questionCount || 0 }} 题</span>
                <span v-if="isTeacherMode">ID: {{ item.id }}</span>
                <span v-if="item.authorName">主备：{{ item.authorName }}</span>
              </div>
              <div v-if="item.summary" class="summary-text">{{ item.summary }}</div>
              <div class="time-line">更新于 {{ formatTime(item.updatedAt) }}</div>
            </div>
            <div class="card-actions">
              <el-dropdown v-if="!isTeacherMode || canEditHandout(item)" trigger="click" @command="cmd => moveHandout(item, cmd)">
                <el-button type="text" icon="el-icon-folder">移动到</el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item :command="0">未分类</el-dropdown-item>
                  <el-dropdown-item v-for="f in folderFlat" :key="f.id" :command="f.id">{{ f.displayName || f.name }}</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
              <el-button type="text" :icon="canEditHandout(item) ? 'el-icon-edit' : 'el-icon-view'" @click="goDetail(item.id)">
                {{ canEditHandout(item) ? '编辑' : '查看' }}
              </el-button>
              <el-button
                v-if="isTeacherMode && !canEditHandout(item)"
                type="text"
                icon="el-icon-document-copy"
                @click="copyHandoutId(item)"
              >复制ID</el-button>
              <template v-if="canEditHandout(item)">
                <el-button type="text" :icon="item.status === 1 ? 'el-icon-download' : 'el-icon-upload2'" @click="toggleStatus(item)">{{ item.status === 1 ? '下架' : '发布' }}</el-button>
              </template>
              <el-button type="text" icon="el-icon-document" @click="exportHandout(item, 'student', 'docx')">学生版 Word</el-button>
              <el-button type="text" icon="el-icon-document-checked" @click="exportHandout(item, 'teacher', 'docx')">教师版 Word</el-button>
              <el-button type="text" icon="el-icon-tickets" @click="exportHandout(item, 'student', 'pdf')">学生版 PDF</el-button>
              <el-button type="text" icon="el-icon-tickets" @click="exportHandout(item, 'teacher', 'pdf')">教师版 PDF</el-button>
              <el-button v-if="canEditHandout(item)" type="text" icon="el-icon-delete" class="danger-text" @click="deleteHandout(item)">删除</el-button>
            </div>
          </div>
        </div>

        <el-pagination class="pagination" @size-change="handleSizeChange" @current-change="handlePageChange" :current-page="page + 1" :page-sizes="[10, 20, 50]" :page-size="size" :total="total" layout="total, sizes, prev, pager, next" />
      </div>
    </div>

    <el-dialog title="新建讲义" :visible.sync="dialogVisible" width="640px" @closed="resetForm">
      <el-form ref="editForm" :model="editForm" :rules="rules" label-width="86px">
        <el-form-item label="讲义名称" prop="title">
          <el-input v-model="editForm.title" placeholder="可自动生成，也可手动修改" />
        </el-form-item>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="学年">
              <el-select v-model="editForm.schoolYearId" placeholder="请选择" style="width:100%" @change="generateTitle">
                <el-option v-for="item in schoolYears" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="年级" prop="gradeId">
              <el-select v-model="editForm.gradeId" placeholder="请选择" style="width:100%" @change="onEditGradeChange">
                <el-option v-for="item in grades" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="学期">
              <el-select v-model="editForm.semesterId" placeholder="请选择" style="width:100%" @change="generateTitle">
                <el-option v-for="item in semesters" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item v-if="!isTeacherMode" label="学科" prop="subjectId">
              <el-select v-model="editForm.subjectId" placeholder="请选择" style="width:100%" @change="generateTitle">
                <el-option v-for="item in subjects" :key="item.id" :label="item.name" :value="item.id" />
              </el-select>
            </el-form-item>
            <el-form-item v-else label="学科">
              <el-input :value="teacherSubjectName" disabled />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="12">
          <el-col :span="12">
            <el-form-item label="课型">
              <el-select v-model="editForm.lessonType" placeholder="请选择" clearable style="width:100%" @change="generateTitle">
                <el-option v-for="item in lessonTypes" :key="item.value" :label="item.label" :value="item.value" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="版本">
              <el-input v-model="editForm.versionNo" placeholder="v1.0" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="主备人">
          <el-input v-model="editForm.authorName" placeholder="管理员/教研老师姓名" />
        </el-form-item>
        <el-form-item v-if="isTeacherMode" label="引用讲义">
          <el-input
            v-model="editForm.sourceHandoutId"
            clearable
            placeholder="可选：粘贴他人讲义 ID，将导入其正文与题目"
          />
          <div class="form-tip">在他人讲义行点击「复制ID」，粘贴到此处后保存即可生成自己的可编辑副本。</div>
        </el-form-item>
        <el-form-item label="讲义说明">
          <el-input v-model="editForm.summary" type="textarea" :rows="3" placeholder="目标、重点、适用班型等" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="saveHandout">保存并编辑</el-button>
      </template>
    </el-dialog>

    <el-dialog :title="folderDialogTitle" :visible.sync="folderDialogVisible" width="400px">
      <el-input v-model="folderForm.name" placeholder="请输入文件夹名称，如：高一化学" maxlength="50" show-word-limit />
      <template #footer>
        <el-button @click="folderDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="folderSaving" @click="saveFolder">确定</el-button>
      </template>
    </el-dialog>

    <HandoutExportPayDialog :visible.sync="exportPayVisible" @paid="onExportPaid" />
  </div>
</template>

<script>
import filterLoader from '../../mixins/filterLoader'
import apiContext from '../../mixins/apiContext'
import handoutExportBilling from '../../mixins/handoutExportBilling'
import teacherRequest from '../../utils/teacherRequest'
import { downloadHandoutExport } from '../../utils/handoutExportDownload'
import { ExportQuotaError } from '../../utils/teacherExportBilling'
import { canTeacherEditHandout } from '../../utils/teacherOwnership'

export default {
  name: 'HandoutList',
  mixins: [filterLoader, apiContext, handoutExportBilling],
  data() {
    return {
      me: null,
      filterForm: { keyword: '', schoolYearId: null, gradeId: null, semesterId: null, subjectId: null, lessonType: null, status: null },
      schoolYears: [],
      grades: [],
      semesters: [],
      subjects: [],
      tableData: [],
      page: 0,
      size: 10,
      total: 0,
      loading: false,
      dialogVisible: false,
      saving: false,
      editForm: this.blankForm(),
      folderTree: [],
      folderFlat: [],
      folderSummary: { totalCount: 0, uncategorizedCount: 0 },
      selectedFolderKey: 'all',
      selectedFolderId: null,
      selectedFolderShared: false,
      folderDialogVisible: false,
      folderDialogMode: 'create',
      folderForm: { name: '', parentId: 0 },
      folderSaving: false,
      lessonTypes: [
        { value: 'NEW', label: '新授课' },
        { value: 'REVIEW', label: '复习课' },
        { value: 'PRACTICE', label: '习题课' },
        { value: 'EXAM_REVIEW', label: '试卷讲评' }
      ],
      rules: {
        title: [{ required: true, message: '请输入讲义名称', trigger: 'blur' }],
        gradeId: [{ required: true, message: '请选择年级', trigger: 'change' }],
        subjectId: [{ required: true, message: '请选择学科', trigger: 'change' }]
      }
    }
  },
  computed: {
    publishedCount() { return this.tableData.filter(item => item.status === 1).length },
    currentFolderLabel() {
      if (this.selectedFolderKey === 'all') return '全部讲义'
      if (this.selectedFolderKey === 'none') return '未分类'
      const folder = this.folderFlat.find(item => item.id === this.selectedFolderId)
      return folder ? folder.name.trim() : ''
    },
    folderDialogTitle() {
      return this.folderDialogMode === 'rename' ? '重命名文件夹' : '新建文件夹'
    },
    createFolderId() {
      if (this.selectedFolderId > 0) return this.selectedFolderId
      return null
    },
    teacherSubjectName() {
      if (this.me && this.me.subjectName) return this.me.subjectName
      return this.getSubjectName(this.editForm.subjectId)
    },
    handoutDetailPath() {
      return this.isTeacherMode ? '/teacher/handout' : '/handout'
    }
  },
  created() {
    if (this.isTeacherMode) {
      teacherRequest.get('/me').then(data => {
        this.me = data
        if (data && data.subjectId) {
          this.editForm.subjectId = data.subjectId
        }
      }).catch(() => {})
    }
    this.loadGradeAndSubject()
    this.loadStageDictBundle(null)
    this.loadDictLookup()
    this.loadFolders()
    this.loadList()
  },
  methods: {
    blankForm() {
      return { title: '', summary: '', schoolYearId: null, gradeId: null, semesterId: null, subjectId: null, lessonType: '', versionNo: 'v1.0', authorName: '', contentHtml: '', folderId: null, sourceHandoutId: '' }
    },
    loadFolders() {
      this.api.get('/handout/folder/tree').then(data => {
        this.folderTree = data.folders || []
        this.folderSummary = data.summary || { totalCount: 0, uncategorizedCount: 0 }
      })
      this.api.get('/handout/folder/flat').then(data => {
        this.folderFlat = data || []
      })
    },
    listFolderIdParam() {
      if (this.selectedFolderKey === 'all') return undefined
      if (this.selectedFolderKey === 'none') return 0
      return this.selectedFolderId
    },
    selectFolder(key, folderId) {
      this.selectedFolderKey = key
      this.selectedFolderId = folderId || null
      this.selectedFolderShared = false
      this.page = 0
      if (this.$refs.folderTree) {
        this.$refs.folderTree.setCurrentKey(null)
      }
      this.loadList()
    },
    onFolderNodeClick(data) {
      this.selectedFolderKey = String(data.id)
      this.selectedFolderId = data.id
      this.selectedFolderShared = !!data.shared
      this.page = 0
      this.loadList()
    },
    handleCreateCommand(command) {
      if (command === 'handout') {
        this.openCreateDialog()
        return
      }
      if (command === 'folder') {
        this.openFolderDialog(this.selectedFolderId > 0 ? this.selectedFolderId : 0)
      }
    },
    openFolderDialog(parentId) {
      this.folderDialogMode = 'create'
      this.folderForm = { name: '', parentId: parentId || 0 }
      this.folderDialogVisible = true
    },
    renameFolder() {
      if (!this.selectedFolderId) return
      const folder = this.folderFlat.find(item => item.id === this.selectedFolderId)
      this.folderDialogMode = 'rename'
      this.folderForm = { name: folder ? (folder.name || '').trim() : '', id: this.selectedFolderId }
      this.folderDialogVisible = true
    },
    saveFolder() {
      if (!this.folderForm.name || !this.folderForm.name.trim()) {
        this.$message.warning('请输入文件夹名称')
        return
      }
      this.folderSaving = true
      const req = this.folderDialogMode === 'rename'
        ? this.api.put(`/handout/folder/${this.folderForm.id}`, { name: this.folderForm.name.trim() })
        : this.api.post('/handout/folder', { name: this.folderForm.name.trim(), parentId: this.folderForm.parentId || 0 })
      req.then(() => {
        this.$message.success(this.folderDialogMode === 'rename' ? '已重命名' : '文件夹已创建')
        this.folderDialogVisible = false
        this.loadFolders()
      }).finally(() => { this.folderSaving = false })
    },
    deleteFolder() {
      if (!this.selectedFolderId) return
      this.$confirm('删除后，该文件夹内的讲义将移至「未分类」，确认删除？', '提示', { type: 'warning' }).then(() => {
        this.api.delete(`/handout/folder/${this.selectedFolderId}`).then(() => {
          this.$message.success('已删除')
          this.selectFolder('all')
          this.loadFolders()
        })
      }).catch(() => {})
    },
    moveHandout(row, folderId) {
      this.api.put(`/handout/folder/move-handout/${row.id}`, { folderId: folderId || null }).then(() => {
        this.$message.success('已移动')
        this.loadList()
        this.loadFolders()
      })
    },
    loadList() {
      this.loading = true
      const folderId = this.listFolderIdParam()
      const params = { ...this.filterForm, page: this.page, size: this.size }
      if (folderId !== undefined) params.folderId = folderId
      this.api.get('/handout/list', { params })
        .then(data => {
          this.tableData = data.content || []
          this.total = data.totalElements || 0
        }).finally(() => { this.loading = false })
    },
    search() { this.page = 0; this.loadList() },
    resetFilters() {
      this.filterForm = { keyword: '', schoolYearId: null, gradeId: null, semesterId: null, subjectId: null, lessonType: null, status: null }
      this.search()
    },
    onGradeChange(gradeId) {
      const grade = this.grades.find(item => item.id === gradeId)
      this.loadStageDictBundle(grade && grade.stageId ? grade.stageId : null).then(this.loadList)
    },
    onEditGradeChange() {
      const grade = this.grades.find(item => item.id === this.editForm.gradeId)
      this.loadStageDictBundle(grade && grade.stageId ? grade.stageId : null).then(this.generateTitle)
    },
    openCreateDialog() {
      this.editForm = this.blankForm()
      if (this.isTeacherMode && this.me && this.me.subjectId) {
        this.editForm.subjectId = this.me.subjectId
      }
      this.dialogVisible = true
    },
    resetForm() {
      this.$refs.editForm && this.$refs.editForm.resetFields()
    },
    generateTitle() {
      const parts = []
      if (this.editForm.schoolYearId) parts.push(this.getSchoolYearName(this.editForm.schoolYearId))
      if (this.editForm.gradeId) parts.push(this.getGradeName(this.editForm.gradeId))
      if (this.editForm.semesterId) parts.push(this.getSemesterName(this.editForm.semesterId))
      if (this.editForm.subjectId) parts.push(this.getSubjectName(this.editForm.subjectId))
      if (this.editForm.lessonType) parts.push(this.lessonTypeLabel(this.editForm.lessonType))
      if (parts.length) this.editForm.title = parts.join('') + '讲义'
    },
    saveHandout() {
      this.$refs.editForm.validate(valid => {
        if (!valid) return
        this.saving = true
        const payload = { ...this.editForm }
        if (this.createFolderId) payload.folderId = this.createFolderId
        if (this.isTeacherMode && this.me && this.me.subjectId) {
          payload.subjectId = this.me.subjectId
        }
        if (!payload.sourceHandoutId) {
          delete payload.sourceHandoutId
        } else {
          const sourceId = Number(String(payload.sourceHandoutId).trim())
          if (!sourceId || Number.isNaN(sourceId)) {
            this.$message.warning('引用讲义 ID 格式不正确')
            this.saving = false
            return
          }
          payload.sourceHandoutId = sourceId
        }
        this.api.post('/handout', payload).then(data => {
          this.$message.success(payload.sourceHandoutId ? '讲义已创建并导入引用内容' : '讲义已创建')
          this.dialogVisible = false
          this.loadFolders()
          this.$router.push(`${this.handoutDetailPath}/${data.id}`)
        }).finally(() => { this.saving = false })
      })
    },
    toggleStatus(row) {
      const status = row.status === 1 ? 0 : 1
      const label = status === 1 ? '发布' : '下架'
      this.$confirm(`确认${label}该讲义？`, '提示', { type: 'warning' }).then(() => {
        this.api.put(`/handout/${row.id}/status`, { status }).then(() => {
          this.$message.success(`已${label}`)
          this.loadList()
        })
      }).catch(() => {})
    },
    deleteHandout(row) {
      this.$confirm('删除后讲义正文和题目关联都会移除，确认删除？', '警告', { type: 'warning' }).then(() => {
        this.api.delete(`/handout/${row.id}`).then(() => {
          this.$message.success('已删除')
          this.loadList()
          this.loadFolders()
        })
      }).catch(() => {})
    },
    async exportHandout(row, version, format = 'docx') {
      const formatLabel = format === 'pdf' ? 'PDF' : 'Word'
      const exec = async () => {
        await downloadHandoutExport(row.id, {
          version,
          format,
          query: this.buildExportQuery(version),
          title: row.title,
          apiBase: this.apiPrefix,
          onError: () => this.$message.error(`${formatLabel} 导出失败`),
          onQuotaExceeded: () => this.openExportPayDialog(exec)
        })
        this.$message.success(`${formatLabel} 已下载`)
        if (this.isTeacherMode) await this.loadExportQuota()
      }
      try {
        await exec()
      } catch (e) {
        if (!(e instanceof ExportQuotaError)) {
          this.$message.error(`${formatLabel} 下载失败`)
        }
      }
    },
    buildExportQuery(version) {
      const params = new URLSearchParams()
      params.set('version', version)
      params.set('showSource', 'true')
      params.set('showDifficulty', 'true')
      params.set('blankPageBreak', 'false')
      params.set('appendQuestions', 'false')
      return params.toString()
    },
    goDetail(id) { this.$router.push(`${this.handoutDetailPath}/${id}`) },
    statusLabel(status) { return ({ 0: '草稿', 1: '已发布', 2: '已归档' })[status] || '未知' },
    statusType(status) { return status === 1 ? 'success' : (status === 2 ? 'info' : 'warning') },
    lessonTypeLabel(value) {
      const item = this.lessonTypes.find(x => x.value === value)
      return item ? item.label : ''
    },
    getGradeName(id) { return (this.grades.find(x => x.id === id) || {}).name || this.getDictDisplayName(id) },
    getSubjectName(id) { return (this.subjects.find(x => x.id === id) || {}).name || this.getDictDisplayName(id) },
    getSchoolYearName(id) { return (this.schoolYears.find(x => x.id === id) || {}).name || this.getDictDisplayName(id) },
    getSemesterName(id) { return (this.semesters.find(x => x.id === id) || {}).name || this.getDictDisplayName(id) },
    formatTime(value) { return value ? value.replace('T', ' ').substring(0, 16) : '' },
    isOwnHandout(item) {
      return canTeacherEditHandout(item, this.me)
    },
    canEditHandout(item) {
      if (!this.isTeacherMode) return true
      return canTeacherEditHandout(item, this.me)
    },
    copyHandoutId(item) {
      const text = String(item.id)
      const done = () => this.$message.success(`已复制讲义 ID：${text}`)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => {
          this.fallbackCopyText(text)
          done()
        })
        return
      }
      this.fallbackCopyText(text)
      done()
    },
    fallbackCopyText(text) {
      const input = document.createElement('textarea')
      input.value = text
      input.style.position = 'fixed'
      input.style.opacity = '0'
      document.body.appendChild(input)
      input.select()
      try { document.execCommand('copy') } catch (e) {}
      document.body.removeChild(input)
    },
    handlePageChange(val) { this.page = val - 1; this.loadList() },
    handleSizeChange(val) { this.size = val; this.page = 0; this.loadList() }
  }
}
</script>

<style scoped>
.handout-layout { display: flex; gap: 16px; align-items: flex-start; }
.folder-panel { width: 220px; flex-shrink: 0; }
.folder-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.folder-title { font-size: 14px; font-weight: 600; color: #333; }
.folder-item { display: flex; align-items: center; gap: 6px; padding: 8px 10px; border-radius: 6px; cursor: pointer; font-size: 13px; color: #555; margin-bottom: 4px; }
.folder-item:hover { background: #f5f7fa; }
.folder-item.active { background: #e6f4ff; color: #1677ff; }
.folder-count { margin-left: auto; font-size: 12px; color: #999; }
.folder-tree { margin-top: 4px; }
.tree-node { display: flex; align-items: center; gap: 4px; flex: 1; font-size: 13px; padding-right: 4px; }
.tree-label { flex: 1; overflow: hidden; text-overflow: ellipsis; }
.folder-empty { font-size: 12px; color: #bbb; padding: 8px 4px; line-height: 1.5; }
.folder-actions { margin-top: 10px; padding-top: 10px; border-top: 1px solid #f0f0f0; display: flex; flex-wrap: wrap; gap: 4px; }
.handout-main { flex: 1; min-width: 0; }
.me-card { margin-bottom: 12px; font-size: 13px; color: #4b5563; display: flex; gap: 16px; align-items: center; flex-wrap: wrap; }
.export-quota-label { color: #909399; font-size: 12px; }
.me-hint { color: #9ca3af; font-size: 12px; }
.filter-card { margin-bottom: 12px; }
.filter-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.create-dropdown { margin-left: auto; }
.folder-breadcrumb { margin-top: 10px; font-size: 13px; color: #6b7280; }
.summary-strip { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-bottom: 12px; }
.summary-item { background: #fff; border: 1px solid #edf0f5; border-radius: 6px; padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; }
.summary-item span { font-size: 13px; color: #6b7280; }
.summary-item strong { font-size: 20px; color: #1f2937; }
.handout-list { display: flex; flex-direction: column; gap: 10px; min-height: 220px; }
.handout-card { display: flex; justify-content: space-between; gap: 16px; background: #fff; border: 1px solid #edf0f5; border-radius: 6px; padding: 16px; }
.handout-card:hover { border-color: #bcd7ff; box-shadow: 0 2px 10px rgba(31, 77, 133, 0.08); }
.card-main { min-width: 0; flex: 1; }
.title-line { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.handout-title { font-size: 16px; font-weight: 700; color: #1f2937; cursor: pointer; }
.handout-title:hover { color: #1677ff; }
.meta-line { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 8px; color: #6b7280; font-size: 12px; }
.meta-line span { background: #f5f7fb; border-radius: 3px; padding: 2px 7px; }
.summary-text { margin-top: 10px; color: #4b5563; font-size: 13px; line-height: 1.6; }
.time-line { margin-top: 10px; color: #9ca3af; font-size: 12px; }
.card-actions { display: flex; align-items: center; flex-wrap: wrap; justify-content: flex-end; min-width: 340px; }
.form-tip { margin-top: 6px; font-size: 12px; color: #9ca3af; line-height: 1.5; }
.danger-text { color: #f56c6c; }
.pagination { margin-top: 16px; text-align: right; }
@media (max-width: 900px) {
  .handout-layout { flex-direction: column; }
  .folder-panel { width: 100%; }
  .summary-strip { grid-template-columns: 1fr; }
  .handout-card { flex-direction: column; }
  .card-actions { min-width: 0; justify-content: flex-start; }
}
</style>
